import React, { useState } from "react";

import capitalize from "lodash/capitalize";

import { getProgramInternalName, useGetAllCarbonPrograms, useGetFarmsByCarbonProgram, useGetPartners } from "utils";

import { CarbonProgram, CarbonProgramParticipation, Partner } from "models";

import { Col, Icon, IconButton, InputSearch, Option, Row, Select, Table, TablePanel, TagNormal, Typography } from "components";

import { AssignCarbonProgramModal } from "pages/admin/carbonPrograms/components/AssignCarbonProgramModal";
import { FarmAreaSummary } from "pages/admin/carbonPrograms/components/FarmAreaSummary";

export const Participation = () => {
  const [selectedProgramId, setSelectedProgramId] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [showAssignCarbonProgramModal, setShowAssignCarbonProgramModal] = useState(false);
  const [farmToAssignProgram, setFarmToAssignProgram] = useState<CarbonProgramParticipation | null>(null);
  const [expandedRows, setExpandedRows] = useState<boolean>(false);

  const { data: allPartnersData } = useGetPartners();

  // Why are we doing this like this?  It leads to duplicate farms showing up in the list and React throwing
  // duplicate key entries... Just fetch all farms where carbonProgramId !== null and then render them
  const { data: allCarbonPrograms } = useGetAllCarbonPrograms();
  const { data: farmsByCarbonProgram, isLoading } = useGetFarmsByCarbonProgram({
    limit,
    page,
    name: searchQuery.length ? searchQuery : undefined,
    carbonProgramId: selectedProgramId.length ? selectedProgramId : undefined,
  });
  const allFarms = farmsByCarbonProgram?.participations || [];

  const carbonProgramsById = (allCarbonPrograms ?? []).reduce(
    (acc, program) => {
      acc[program.id] = program;
      return acc;
    },
    {} as Record<string, CarbonProgram | undefined>
  );

  const partnersById = (allPartnersData?.partners ?? []).reduce(
    (acc, partner) => {
      acc[partner.id] = partner;
      return acc;
    },
    {} as Record<string, Partner | undefined>
  );

  const data = allFarms.map((farm) => {
    const carbonProgram = carbonProgramsById[farm.carbonProgramId ?? ""];
    const partner = partnersById[carbonProgram?.partnerId ?? ""];

    return {
      key: farm.id,
      "Farm name": <TablePanel headline={farm.name} />,
      "Program name": <Typography variant="paragraph-m">{carbonProgram ? getProgramInternalName(carbonProgram) : ""}</Typography>,
      "Partner name": <Typography variant="paragraph-m">{partner?.name ?? ""}</Typography>,
      Methodology: <Typography variant="paragraph-m">{capitalize(carbonProgram?.accountingMethodology ?? "")}</Typography>,
      Country: <Typography variant="paragraph-m">{farm.country}</Typography>,
      "Farm region": <Typography variant="paragraph-m">{farm.region}</Typography>,
      "Farm ha limit": <Typography variant="paragraph-m">{farm.maxHectares}</Typography>,
      "Enrolled fields": (
        <Typography variant="paragraph-m">
          {farm.enrolledFieldCount} / {farm.totalFieldCount}
        </Typography>
      ),
      "Ha with enrolled practices": (
        <Typography variant="paragraph-m">
          {farm.enrolledHectares} / {farm.totalHectares}
        </Typography>
      ),
      Comment: <Typography variant="paragraph-m">{farm.comment}</Typography>,
      Tags: farm.tags?.map((tag, i) => <TagNormal key={i} text={tag} />),
      "Assign  program": (
        <IconButton
          iconName="tabler/timeline-event-plus"
          tooltip={{ title: "Assign program", placement: "top" }}
          onClick={() => handleOpenAssignCarbonProgramModal(farm)}
          type="secondary"
        />
      ),
      expandable: (
        <>
          <FarmAreaSummary fieldIds={farm.fieldIds} farmId={farm.id} carbonProgram={carbonProgram} />
        </>
      ),
      " ": <Icon className="icon inline" iconName={`tabler/chevron-${expandedRows ? "up" : "down"}`} />,
    };
  });

  const handleOpenAssignCarbonProgramModal = (farm: CarbonProgramParticipation) => {
    setFarmToAssignProgram(farm);
    setShowAssignCarbonProgramModal(true);
  };

  const handleCloseAssignCarbonProgramModal = () => {
    setFarmToAssignProgram(null);
    setShowAssignCarbonProgramModal(false);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  return (
    <>
      <Typography variant="paragraph-l" className="mb-6">
        View and assign farms to carbon programs. At the moment, carbon programs cannot be changed, so please be careful. We will launch that functionality
        soon.
      </Typography>
      <div className="carbon-participation-farm-seach">
        <Typography variant="h3" className="margin-bottom-150">
          {farmsByCarbonProgram?.count} farms
        </Typography>
        <Row>
          <Col>
            <div className="carbon-participation-search-container">
              <InputSearch
                className="carbon-participation-search"
                label="Farm search"
                value={searchQuery ?? ""}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
              <div>
                <Select
                  className="carbon-participation-filter"
                  value={selectedProgramId}
                  onChange={setSelectedProgramId}
                  label="Carbon programs"
                  defaultActiveFirstOption={false}
                  defaultValue=""
                  disabled={false}
                  popupMatchSelectWidth={true}
                  showArrow={true}
                  showSearch={true}
                  optionLabelProp="children"
                  dropdownStyle={undefined}
                  filterOption={undefined}
                  initialValue={undefined}
                  message={undefined}
                  mode={undefined}
                  name={undefined}
                  optionFilterProp={undefined}
                  rules={undefined}
                  style={undefined}
                  onSearch={undefined}
                  dataTestId={undefined}
                >
                  <Typography variant="paragraph-m" key="" data-value="">
                    All Carbon Programs
                  </Typography>
                  {(allCarbonPrograms ?? []).map((program) => (
                    <Option key={program.id} value={program.id}>
                      {getProgramInternalName(program)}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>
          </Col>
        </Row>
      </div>
      <Row>
        <Col>
          <Table
            data={data}
            className="farms"
            loading={isLoading}
            pagination={{
              total: farmsByCarbonProgram?.count,
              defaultPageSize: 10,
              pageSize: limit,
              current: page,
              onChange: (newPage, newLimit) => {
                if (newLimit !== limit) {
                  setLimit(newLimit);
                  setPage(1);
                } else {
                  setLimit(limit);
                  setPage(newPage);
                }
              },
            }}
            expandable={{
              onExpand: (e) => setExpandedRows(e),
              expandedRowRender: (item) => <>{item.expandable}</>,
            }}
          />
        </Col>
      </Row>
      <AssignCarbonProgramModal
        showModal={showAssignCarbonProgramModal}
        onClose={handleCloseAssignCarbonProgramModal}
        programParticipation={farmToAssignProgram}
      />
    </>
  );
};

export default Participation;
